import { useEffect } from 'react';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import WhatsAppButton from '../../home/components/WhatsAppButton';

export default function ArizaTeshisPage() {
  useSEO({
    title: 'Arıza Teşhis ve Elektronik Sistem | Denizli Araç Servisi',
    description: 'Denizli\'de son teknoloji cihazlarla arıza teşhis ve elektronik sistem onarımı. Marka özel teşhis cihazları, osiloskop, termal kamera ile hassas arıza tespiti. Motor arıza lambası, ABS, ESP onarımı.',
    keywords: 'denizli arıza teşhis, denizli elektronik arıza, denizli motor arıza lambası, denizli abs tamiri, denizli esp tamiri, denizli araç teşhis, elektronik sistem tamiri denizli',
    canonical: '/blog/ariza-teshis-elektronik-sistem',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Arıza Teşhis & Elektronik Sistem',
      description: 'Son teknoloji cihazlarla hassas arıza tespiti ve onarım. Marka özel teşhis ekipmanları ile profesyonel hizmet.',
      author: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png',
        },
      },
      datePublished: '2024-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/ariza-teshis-elektronik-sistem`,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/78a545fe3952e988d2e0a128ba5ba453.jpg"
          alt="Arıza Teşhis"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-6">Arıza Teşhis &amp; Elektronik Sistem</h1>
          <p className="font-sans text-xl text-gray-200">Son teknoloji cihazlarla hassas arıza tespiti ve onarım</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
              Modern araçlar, karmaşık elektronik sistemlerle donatılmıştır. Motor yönetiminden güvenlik sistemlerine, konfor özelliklerinden multimedya sistemlerine kadar her şey elektronik kontrol altındadır. Servisimiz, son teknoloji teşhis ekipmanları ve uzman kadrosuyla, en karmaşık elektronik arızaları bile hızlı ve doğru bir şekilde tespit edip çözer.
            </p>
          </div>

          {/* Modern Araç Elektroniği */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Modern Araç Elektroniği</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-4">
              Günümüz araçlarında 50'den fazla elektronik kontrol ünitesi (ECU) bulunabilir. Bu sistemler sürekli birbirleriyle iletişim halindedir ve herhangi birindeki sorun, diğerlerini de etkileyebilir. Elektronik sistemler:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-cpu-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Motor Yönetim Sistemi:</strong> Yakıt enjeksiyonu, ateşleme, emisyon kontrolü</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-cpu-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Şanzıman Kontrol Ünitesi:</strong> Otomatik vites değişimi ve optimizasyon</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-cpu-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>ABS/ESP Sistemi:</strong> Fren ve sürüş stabilitesi kontrolü</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-cpu-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Hava Yastığı Sistemi:</strong> Çarpışma algılama ve güvenlik</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-cpu-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Konfor Sistemleri:</strong> Klima, aydınlatma, multimedya</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-cpu-line text-accent-gold text-xl mr-3 mt-1"></i>
                <span><strong>Sürüş Destek Sistemleri:</strong> Park sensörü, kamera, şerit takip</span>
              </li>
            </ul>
          </div>

          {/* Image */}
          <div className="mb-16 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=automotive%20diagnostic%20computer%20screen%20displaying%20vehicle%20system%20data%20and%20error%20codes%2C%20professional%20technician%20analyzing%20electronic%20control%20unit%20information%2C%20modern%20diagnostic%20interface%20with%20graphs%20and%20technical%20data&width=1200&height=600&seq=blog-ariza-002&orientation=landscape"
              alt="Elektronik Teşhis"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Teşhis Ekipmanlarımız */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">İleri Teknoloji Teşhis Ekipmanlarımız</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Doğru teşhis, doğru onarımın ilk adımıdır. Servisimizde bulunan son teknoloji ekipmanlar:
            </p>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-computer-line text-accent-gold text-2xl mr-3"></i>
                  Marka Özel Teşhis Cihazları
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Mercedes XENTRY, BMW ISTA, Audi/VW ODIS, Porsche PIWIS gibi orijinal teşhis sistemleri. Bu cihazlar, üretici seviyesinde detaylı bilgiye erişim sağlar, kodlama ve programlama işlemlerini mümkün kılar. Genel teşhis cihazlarının göremediği derin sistem ayarlarına ulaşılır.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-line-chart-line text-accent-gold text-2xl mr-3"></i>
                  Osiloskop ve Multimetre
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Elektrik sinyallerini gerçek zamanlı olarak analiz eden profesyonel osiloskoplar. Sensör sinyalleri, aktüatör çalışmaları, CAN-BUS iletişimi gibi karmaşık elektrik sorunlarının tespitinde kullanılır. Aralıklı arızaların yakalanmasında kritik öneme sahiptir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-camera-line text-accent-gold text-2xl mr-3"></i>
                  Endoskopik Kamera Sistemleri
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Motor içi, egzoz sistemi, hava kanalları gibi görülmesi zor alanlarda kullanılan endoskopik kameralar. Parça sökmeden içerideki durumu görüntüleme imkanı sağlar. Karbon birikimi, yağ sızıntısı, mekanik hasarlar kolayca tespit edilir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-temp-hot-line text-accent-gold text-2xl mr-3"></i>
                  Termal Kamera
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Sıcaklık dağılımını görüntüleyen termal kameralar. Aşırı ısınan bileşenler, soğutma sistemi sorunları, elektrik kablo problemleri kolayca tespit edilir. Özellikle aralıklı elektrik arızalarında çok etkilidir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-sound-module-line text-accent-gold text-2xl mr-3"></i>
                  Akustik Arıza Tespit Cihazı
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed">
                  Anormal sesleri tespit eden özel mikrofonlar ve yazılımlar. Motor içi sesler, rulman gürültüleri, egzoz kaçakları gibi işitsel arızaların kaynağını hassas bir şekilde belirler.
                </p>
              </div>
            </div>
          </div>

          {/* Arıza Teşhis Süreci */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Profesyonel Arıza Teşhis Sürecimiz</h2>
            
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="bg-accent-gold text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold text-lg">1</div>
                <div>
                  <h3 className="font-sans text-xl font-semibold text-gray-900 mb-2">Müşteri Görüşmesi ve Ön Değerlendirme</h3>
                  <p className="font-sans text-base text-gray-700 leading-relaxed">
                    Arızanın ne zaman başladığı, hangi koşullarda ortaya çıktığı, eşlik eden belirtiler detaylı olarak sorgulanır. Bu bilgiler, teşhis sürecini hızlandırır ve doğru yönlendirme sağlar.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-accent-gold text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold text-lg">2</div>
                <div>
                  <h3 className="font-sans text-xl font-semibold text-gray-900 mb-2">Elektronik Sistem Taraması</h3>
                  <p className="font-sans text-base text-gray-700 leading-relaxed">
                    Tüm elektronik kontrol üniteleri taranır, hata kodları okunur. Sadece aktif hatalar değil, geçmiş hatalar ve donmuş veriler de incelenir. CAN-BUS iletişimi kontrol edilir.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-accent-gold text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold text-lg">3</div>
                <div>
                  <h3 className="font-sans text-xl font-semibold text-gray-900 mb-2">Canlı Veri Analizi</h3>
                  <p className="font-sans text-base text-gray-700 leading-relaxed">
                    Motor çalışırken veya test sürüşü sırasında, sensör değerleri gerçek zamanlı olarak izlenir. Normal değerlerden sapma gösteren parametreler belirlenir. Aralıklı arızalar için uzun süreli kayıt yapılır.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-accent-gold text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold text-lg">4</div>
                <div>
                  <h3 className="font-sans text-xl font-semibold text-gray-900 mb-2">Fiziksel Kontrol ve Ölçümler</h3>
                  <p className="font-sans text-base text-gray-700 leading-relaxed">
                    Şüpheli bileşenler fiziksel olarak kontrol edilir. Kablo bağlantıları, konektörler, sensörler incelenir. Gerekirse osiloskop ile elektrik sinyalleri ölçülür, direnç ve voltaj testleri yapılır.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-accent-gold text-white w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold text-lg">5</div>
                <div>
                  <h3 className="font-sans text-xl font-semibold text-gray-900 mb-2">Teşhis Raporu ve Çözüm Önerisi</h3>
                  <p className="font-sans text-base text-gray-700 leading-relaxed">
                    Tüm bulgular detaylı bir raporda sunulur. Arızanın nedeni, gerekli onarımlar ve tahmini maliyet açıklanır. Müşteri onayı alındıktan sonra onarım sürecine geçilir.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sık Karşılaşılan Arızalar */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Sık Karşılaşılan Elektronik Arızalar</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-error-warning-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Motor Arıza Lambası (Check Engine)</h4>
                <p className="font-sans text-sm text-gray-600">Emisyon sistemi, sensör arızaları, yanma problemleri</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-steering-2-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">ABS/ESP Arıza Lambası</h4>
                <p className="font-sans text-sm text-gray-600">Tekerlek hız sensörleri, hidrolik ünite sorunları</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-shield-cross-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Hava Yastığı Arıza Lambası</h4>
                <p className="font-sans text-sm text-gray-600">Sensör, kablo veya kontrol ünitesi problemleri</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-battery-low-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Şarj Sistemi Arızası</h4>
                <p className="font-sans text-sm text-gray-600">Alternatör, akü veya voltaj regülatörü sorunları</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-settings-4-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Şanzıman Arıza Lambası</h4>
                <p className="font-sans text-sm text-gray-600">TCU, sensör veya mekatronik ünite problemleri</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-temp-hot-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Aşırı Isınma Uyarısı</h4>
                <p className="font-sans text-sm text-gray-600">Soğutma sistemi, termostat veya sensör arızaları</p>
              </div>
            </div>
          </div>

          {/* Üst Segment Uzmanlığı */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">Üst Segment Araç Elektroniği Uzmanlığı</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              Mercedes, BMW, Audi, Porsche gibi premium markaların elektronik sistemleri son derece karmaşıktır. Bu araçlarda:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Orijinal teşhis cihazları ve yazılımları kullanılır</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Kodlama ve programlama işlemleri yapılabilir</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Yazılım güncellemeleri ve kalibrasyonlar gerçekleştirilir</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Özel donanım ve ekipmanlar mevcuttur</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-star-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Uzman teknisyenlerimiz düzenli eğitim alır</span>
              </li>
            </ul>
          </div>

          {/* Güvenilirlik */}
          <div className="mb-16 bg-primary-dark rounded-2xl p-10">
            <h2 className="font-serif text-3xl font-bold text-white mb-6">Güvenilirlik ve Kalite</h2>
            <p className="font-sans text-base text-gray-300 leading-relaxed mb-4">
              Arıza teşhis ve elektronik sistem hizmetlerimizde kalite ve güvenilirlik önceliğimizdir:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Orijinal veya OEM parça kullanımı</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Elektronik sistem uzmanı teknisyenler</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Son teknoloji teşhis ekipmanları</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Detaylı test ve kalite kontrol</span>
              </li>
              <li className="font-sans text-base text-gray-300 flex items-start">
                <i className="ri-shield-check-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Tüm işlemler e-karneye kaydedilir</span>
              </li>
            </ul>
          </div>

          {/* Önleyici Bakım */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Elektronik Sistem Bakımı ve Önleyici İşlemler</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Elektronik arızaların çoğu önlenebilir. Düzenli bakım ve kontroller ile:
            </p>
            <div className="bg-gray-50 p-8 rounded-xl">
              <ul className="space-y-4">
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Akü durumu ve şarj sistemi düzenli kontrol edilmeli</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Kablo bağlantıları ve konektörler temiz tutulmalı</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Yazılım güncellemeleri zamanında yapılmalı</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Su ve nem elektronik bileşenlerden uzak tutulmalı</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Arıza lambaları yandığında hemen kontrol ettirilmeli</span>
                </li>
                <li className="font-sans text-base text-gray-700 flex items-start">
                  <i className="ri-arrow-right-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                  <span>Yıllık elektronik sistem check-up yaptırılmalı</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gray-50 rounded-2xl p-10 text-center">
            <h3 className="font-serif text-3xl font-bold text-gray-900 mb-4">Aracınızda Elektronik Arıza mı Var?</h3>
            <p className="font-sans text-base text-gray-700 mb-8 max-w-2xl mx-auto">
              Arıza lambası yandı mı? Anormal davranışlar mı gözlemliyorsunuz? Hemen randevu alın, uzman kadromuz aracınızı son teknoloji cihazlarla teşhis etsin.
            </p>
            <a
              href="/#appointment"
              className="inline-block bg-accent-gold text-primary-dark px-8 py-4 rounded-lg font-sans font-semibold hover:bg-primary-dark hover:text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Ücretsiz Ön Teşhis
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}